import { useAppSelector } from '@casops/store/hooks';
import { getFilterNamesFromFiltersList, getFiltersList } from './utils';
import { FilterGroup } from './types';
import { defaultState } from '@casops/store/slices/filtersSlice';
import { useMemo } from 'react';

/**
 * Determine if any of the filters in the subset have been modified from their initial state.
 */
const useHasFilters = (key: 'draft' | 'saved', filterGroup?: FilterGroup): boolean => {
  const filters = useAppSelector(state => state.filters[key]);

  const hasSubsetFilters = useMemo<boolean>(() => {
    if (!filterGroup) {
      return false;
    }

    const filterNames = getFilterNamesFromFiltersList(getFiltersList(filterGroup));

    return filterNames.some(filterName => {
      const filterValue = filters[filterName];
      const defaultFilterValue = defaultState[key][filterName];

      if (Array.isArray(filterValue) && Array.isArray(defaultFilterValue)) {
        // Compare array contents
        return (
          filterValue.length !== defaultFilterValue.length ||
          filterValue.some((v, i) => v !== defaultFilterValue[i])
        );
      }
      return filterValue !== defaultFilterValue;
    });
  }, [filterGroup, key, filters]);

  return hasSubsetFilters;
};

export const useHasSavedFilters = (filterGroup?: FilterGroup): boolean => {
  return useHasFilters('saved', filterGroup);
};

export const useHasDraftFilters = (filterGroup?: FilterGroup): boolean => {
  return useHasFilters('draft', filterGroup);
};
